import type { LoopState, PermanentState } from "../types"
import { formatCalendarDate } from "../game/calendarEngine"
import { getFactionById } from "../game/content"
import { FrameCorners } from "../components/Framed"

interface DeathScreenProps {
  permanent: PermanentState
  loop: LoopState
  onAwaken: () => void
}

export function DeathScreen({ permanent, loop, onAwaken }: DeathScreenProps) {
  // The loop can end without the player being killed: on Day 512, with the
  // Necromancer still alive, the ritual completes and the sun goes out. It is a
  // loss of the whole world rather than of one life, and it reads differently —
  // but it turns the loop over by the same door, so it is handled here.
  const sunWentOut = loop.worldFlags.includes("sun_extinguished")

  // A realm the rival forged the player out of read the false evidence, called
  // its guard, and the player lost the fight — which, for a proven traitor, is
  // a hanging rather than a soldier's death (see accusationEngine).
  const hangedBy = loop.worldFlags.includes("executed_by_accusation")
    ? loop.worldFlags.find((flag) => flag.startsWith("hanged_by_"))
    : undefined

  if (hangedBy) {
    const factionId = hangedBy.slice("hanged_by_".length)
    const factionName = getFactionById(factionId)?.name ?? "a realm you had hoped to win"
    return (
      <div className="screen death-screen framed">
        <FrameCorners />
        <h1>You Were Hanged</h1>
        <p>
          On {formatCalendarDate(loop.currentDay)} of Loop {loop.loopNumber}, a
          court of {factionName} read a sheaf of letters in your own hand and
          believed every word of them. You could not talk your way clear, and
          you could not cut your way out. They hang traitors, and to them that is
          what you were — because a grey-haired man with a very good memory made
          sure of it. The letters were forgeries. The rope was not.
        </p>
        <dl className="status-line">
          <dt>Total Deaths</dt>
          <dd>{permanent.totalDeaths}</dd>
          <dt>Longest Survival</dt>
          <dd>{permanent.longestSurvival} days</dd>
        </dl>
        <button type="button" onClick={onAwaken}>
          Awaken on {formatCalendarDate(1)}
        </button>
      </div>
    )
  }

  if (sunWentOut) {
    return (
      <div className="screen death-screen framed">
        <FrameCorners />
        <h1>The Sun Went Out</h1>
        <p>
          You reached the five hundred and twelfth day of Loop {loop.loopNumber}{" "}
          with the Necromancer still alive. The ritual completed, the sun was
          pinched dark, and the cold came down all at once. You were not too weak
          this time. You were too slow.
        </p>
        <dl className="status-line">
          <dt>Total Deaths</dt>
          <dd>{permanent.totalDeaths}</dd>
          <dt>Longest Survival</dt>
          <dd>{permanent.longestSurvival} days</dd>
        </dl>
        <button type="button" onClick={onAwaken}>
          Awaken on {formatCalendarDate(1)}
        </button>
      </div>
    )
  }

  return (
    <div className="screen death-screen framed">
      <FrameCorners />
      <h1>You Have Died</h1>
      <p>
        You survived until {formatCalendarDate(loop.currentDay)} of Loop {loop.loopNumber}.
      </p>
      <dl className="status-line">
        <dt>Total Deaths</dt>
        <dd>{permanent.totalDeaths}</dd>
        <dt>Longest Survival</dt>
        <dd>{permanent.longestSurvival} days</dd>
      </dl>
      <button type="button" onClick={onAwaken}>
        Awaken on {formatCalendarDate(1)}
      </button>
    </div>
  )
}
